import { AgentThreadVoStatus, type AgentThreadVo } from "@/api";
import { useToast } from "@/component/Toast/useToast";
import { t } from "@/i18n";
import { chatStore } from "@/store/chat";
import { threadStore } from "@/store/thread";
import { Attachment, FileAttachment, UrlAttachment } from "@/types/attachment";
import { AspectRatio, Resolution } from "@/types/common";
import { type ChatDraft } from "@/types/draft-strorage";
import { ModelConfigKey } from "@/types/modelConfig";
import {
  attachmentsToPersisted,
  persistedToAttachments,
} from "@/util/draftConverter";
import {
  deleteDraft,
  getDraft,
  getDraftKey,
  setDraft,
} from "@/util/draftStorage";
import { uploadAttachments } from "@/util/uploadAttachments";
import { subscribeToWebPush } from "@/util/webPush";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { VidMuseEvent } from "../common/events";
import { logger } from "../common/logger";
import { useFileUpload } from "./useFileUpload";

const stopTooltipText = "Stop creating";
const stoppingTooltipText = "Stopping...";

export function useSendMessage(
  thread?: AgentThreadVo,
  chatBoxRef?: React.RefObject<HTMLDivElement | null>,
  initialText?: string,
  onAttachmentSuccess?: (attachment: Attachment) => void,
  aspectRatio?: AspectRatio,
  resolution?: Resolution,
  modelConfig?: Partial<Record<ModelConfigKey, string>>,
  knowledgeBaseId?: string
) {
  const [text, setText] = useState(initialText || "");
  const [isAuto, setIsAuto] = useState<boolean>(thread?.autoMode ?? true);
  const [isSending, setIsSending] = useState(false);
  const [isRequestStopping, setIsRequestStopping] = useState(false);
  const router = useRouter();
  const { error: showErrorToast } = useToast();
  const threadId = thread?.id;

  // Toast 显示逻辑
  const showSendMessageToast = useCallback(
    (content: string) => {
      if (!content) return;

      const options = {
        key: "input-upload-error",
      } as {
        key: string;
        style?: { marginTop: string; transform: string };
      };

      // 如果在首页，Toast 需要弹到临近输入框的位置
      const isHomePage = !threadId;
      if (isHomePage && chatBoxRef?.current) {
        const { y } = chatBoxRef.current.getBoundingClientRect();
        const topPos = Math.floor(y - 30);

        if (topPos > 0) {
          options.style = {
            marginTop: `${topPos}px`,
            transform: `translateY(-100%)`,
          };
        }
      }

      showErrorToast(content, options);
    },
    [showErrorToast, threadId, chatBoxRef]
  );

  // 使用通用文件上传 hook
  const {
    attachmentList,
    setAttachmentList,
    uploadProps,
    onRemove,
    clearAttachments,
  } = useFileUpload({
    accept:
      "image/*,video/*,audio/*,.mp3,audio/mpeg,.wav,audio/wav,.m4a,audio/x-m4a,.aac,audio/aac",
    onSuccess: (attachment) => {
      onAttachmentSuccess?.(attachment);
    },
    onError: (errors) => {
      if (errors.tooMany) {
        showSendMessageToast("You can upload up to 5 files per project.");
      }
      if (errors.oversized) {
        showSendMessageToast(
          "Maximum total upload size is 100MB. Try compressing them before uploading."
        );
      }
    },
  });

  // 为不同会话/页面创建唯一草稿键
  const draftKey = useMemo(() => getDraftKey(threadId), [threadId]);

  // 恢复草稿
  useEffect(() => {
    // 如果提供了 initialText，优先使用 initialText，不恢复草稿
    if (initialText) {
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const draft = await getDraft(draftKey);
        if (!isMounted || !draft) return;

        if (typeof draft.text === "string") {
          setText(draft.text);
        }
        if (typeof draft.isAuto === "boolean") {
          setIsAuto(draft.isAuto);
        }
        if (Array.isArray(draft.attachments) && draft.attachments.length > 0) {
          const restoredAttachments = await persistedToAttachments(
            draft.attachments
          );
          if (isMounted) {
            setAttachmentList(restoredAttachments);
          }
        }
      } catch (error) {
        console.error(error);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [draftKey, setAttachmentList, initialText]);

  // 保存草稿（节流/防抖）
  useEffect(() => {
    const handler = setTimeout(async () => {
      try {
        const attachments = await attachmentsToPersisted(attachmentList);
        const draft: ChatDraft = {
          text: text.trim(),
          isAuto,
          attachments,
          updatedAt: Date.now(),
        };
        await setDraft(draftKey, draft);
      } catch (error) {
        console.error(error);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [text, isAuto, attachmentList, draftKey]);

  const handleContinue = useCallback(async () => {
    if (!threadId || isSending) {
      return;
    }

    setIsSending(true);

    const content = t("messages.continue");

    chatStore.addOptimisticUserMessage(content, []);

    try {
      await threadStore.sendMessage(threadId, {
        files: [],
        text: content,
      });

      if (thread) {
        threadStore.set("thread", {
          ...thread,
          status: AgentThreadVoStatus.Running,
        });
      }

      logger.event(VidMuseEvent.ThreadContinue, {
        thread_id: threadId,
      });
    } catch (error) {
      chatStore.removeOptimisticUserMessage();
      console.error("Failed to continue:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to continue. Please try again.";
      showSendMessageToast(errorMessage);
    } finally {
      setIsSending(false);
    }
  }, [threadId, thread, showSendMessageToast]);

  const handleSendMessage = useCallback(async () => {
    const content = text.trim();

    if (!content) {
      showSendMessageToast("Please enter your video idea to get started");
      return;
    }

    setIsSending(true);

    if (threadId) {
      const files = attachmentList
        .filter((item): item is FileAttachment => item.type === "file")
        .map((item) => item.file);
      const urls = attachmentList
        .filter((item): item is UrlAttachment => item.type === "url")
        .map((item) => item.url);

      try {
        chatStore.addOptimisticUserMessage(content, files, urls);
        setText("");
        setAttachmentList([]);
        clearAttachments();

        await threadStore.sendMessage(threadId, {
          files,
          text: content,
          urls,
        });

        if (thread) {
          threadStore.set("thread", {
            ...thread,
            status: AgentThreadVoStatus.Running,
          });
        }

        // 提交成功后清理草稿
        await deleteDraft(draftKey);

        logger.event(VidMuseEvent.ThreadSendMessage, {
          thread_id: threadId,
          prompt: content,
          attachment_count: attachmentList.length,
        });
      } catch (e) {
        console.error("Failed to send message:", e);
        const errorMessage =
          e instanceof Error
            ? e.message
            : "Failed to send message. Please try again.";
        showSendMessageToast(errorMessage);
      } finally {
        setIsSending(false);
      }
    } else {
      try {
        // 先上传所有附件，获取 URLs
        const urls = await uploadAttachments(attachmentList);

        const res = await threadStore.createThread({
          urls,
          user_input: content,
          auto_mode: isAuto,
          aspect_ratio: aspectRatio || AspectRatio.LANDSCAPE,
          resolution: resolution || Resolution.HD,
          knowledge_base_id: knowledgeBaseId,
          default_models: modelConfig,
        });

        if (res.id) {
          // 提交成功后清理"新建"草稿
          await deleteDraft(draftKey);
          router.push(`/thread/${res.id}`);

          logger.event(VidMuseEvent.HomePageCreateThread, {
            thread_id: res.id,
            prompt: content,
            attachment_count: attachmentList.length,
          });
        }
      } catch (e) {
        console.error("Failed to create thread:", e);
        const errorMessage =
          e instanceof Error
            ? e.message
            : "Failed to create thread. Please try again.";
        showSendMessageToast(errorMessage);
        setIsSending(false);
      }
    }

    subscribeToWebPush();
  }, [
    text,
    attachmentList,
    showSendMessageToast,
    threadId,
    thread,
    isAuto,
    aspectRatio,
    knowledgeBaseId,
    router,
    draftKey,
    clearAttachments,
    setAttachmentList,
  ]);

  const handleTextAreaKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  const handleStop = useCallback(async () => {
    if (!threadId) {
      return;
    }
    try {
      setIsRequestStopping(true);
      await threadStore.stopRunning();
    } finally {
      setIsRequestStopping(false);
    }
  }, [threadId]);

  return {
    text,
    isAuto,
    handleMessageTextChange: setText,
    handleSendMessage,
    handleContinue,
    attachmentList,
    clearAttachments,
    setAttachmentList,
    handleTextAreaKeyDown,
    uploadProps,
    onRemove,
    isSending,
    stopTooltipText,
    stoppingTooltipText,
    isRequestStopping,
    handleStop,
  };
}
